class AvenoriFunnel {
  constructor(root) {
    this.root = root;
    // The PDP keeps the one and only buy form inside the hero, while older
    // long-form builds placed it in a standalone .av-offer section. The shared
    // #offer anchor is the stable contract across both layouts.
    this.offer = root.querySelector('#offer');
    this.scrollContainer = this.findScrollContainer();
    this.sticky = root.querySelector('[data-av-sticky]');
    this.quantityInput = root.querySelector('[data-av-quantity-input]');
    this.total = root.querySelector('[data-av-total]');
    this.bundleInputs = [...root.querySelectorAll('[data-av-bundle]')];
    this.galleryImage = root.querySelector('.av-gallery__image');
    this.galleryZoom = root.querySelector('[data-av-gallery-open]');
    this.galleryThumbs = [...root.querySelectorAll('[data-av-gallery-thumb]')];
    this.galleryVideos = [...root.querySelectorAll('[data-av-gallery-video]')];
    this.galleryStage = root.querySelector('.av-gallery__stage');
    this.gallerySwipeHandledUntil = 0;
    this.lightbox = root.querySelector('[data-av-gallery-lightbox]');
    this.lightboxImage = root.querySelector('[data-av-gallery-lightbox-image]');
    this.lightboxOpen = false;
    this.galleryImageCache = new Set();
    this.onLightboxKeydown = this.onLightboxKeydown.bind(this);
    this.addButton = root.querySelector('.av-add-button');

    this.stickyQty = root.querySelector('[data-av-sticky-qty]');
    this.stickyTotal = root.querySelector('[data-av-sticky-total]');
    this.bindBundles();
    this.bindGallery();
    this.bindSticky();
    this.bindCartEvents();
    this.bindCtas();
    this.bindHeroQuotes();
    this.bindReviews();
  }

  bindHeroQuotes() {
    const carousel = this.root.querySelector('[data-av-heroquotes]');
    if (!carousel) return;

    const quotes = [...carousel.querySelectorAll('[data-av-heroquote]')];
    const previous = carousel.querySelector('[data-av-heroquotes-previous]');
    const next = carousel.querySelector('[data-av-heroquotes-next]');
    if (quotes.length < 2 || !previous || !next) return;

    let activeIndex = Math.max(0, quotes.findIndex((quote) => !quote.hidden));

    const show = (index) => {
      activeIndex = (index + quotes.length) % quotes.length;
      quotes.forEach((quote, quoteIndex) => {
        quote.hidden = quoteIndex !== activeIndex;
      });
    };

    previous.addEventListener('click', () => show(activeIndex - 1));
    next.addEventListener('click', () => show(activeIndex + 1));

    carousel.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      show(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });

    show(activeIndex);
  }

  /**
   * Every CTA above and below the buy box is an <a href="#offer">. Two problems
   * with leaving that to the browser: the theme sets scroll-behavior: smooth,
   * which on a page this tall is a slow ride, and the anchor lands under the
   * sticky header.
   *
   * So we take over the click. Default is a fast eased scroll with the header
   * height subtracted. Set the section to "checkout" and the same buttons add
   * the preselected bundle and go straight to checkout instead.
   */
  bindCtas() {
    const data = this.root.dataset;
    this.ctaBehaviour = data.ctaBehaviour === 'checkout' ? 'checkout' : 'scroll';
    this.ctaScrollSpeed = Number.parseInt(data.ctaScrollSpeed ?? '300', 10);

    // If the buy box is not on this page there is nothing to take over — leave
    // the anchors alone so the browser's own behaviour still applies.
    if (!this.offer) return;

    // Sending someone to a checkout for a variant that cannot be bought lands
    // them on an empty or broken checkout, so fall back to the chooser.
    if (this.ctaBehaviour === 'checkout' && data.variantAvailable !== 'true') {
      this.ctaBehaviour = 'scroll';
    }

    const ctas = [...document.querySelectorAll('a[href="#offer"], a[href$="#offer"]')];
    if (!ctas.length) return;

    ctas.forEach((cta) => {
      if (cta.dataset.avCtaBound === 'true') return;
      cta.dataset.avCtaBound = 'true';

      cta.addEventListener('click', (event) => {
        // Let modified clicks (new tab, download, middle click) behave normally.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }

        event.preventDefault();

        if (this.ctaBehaviour === 'checkout') {
          this.goToCheckout(cta);
        } else {
          this.scrollToOffer();
        }
      }, { capture: cta.hasAttribute('data-av-offer-cta') });
    });
  }

  bindReviews() {
    const root = this.root.querySelector('[data-av-reviews]');
    if (!root) return;

    const list = root.querySelector('[data-av-reviews-list]');
    if (!list) return;

    let items = [...list.querySelectorAll('[data-av-review]')];
    const moreBtn = root.querySelector('[data-av-reviews-more]');
    const sortSelect = root.querySelector('[data-av-reviews-sort]');
    const filters = [...root.querySelectorAll('[data-av-star-filter]')];
    const pageSize = 5;
    let shown = pageSize;
    let starFilter = 0;

    const matching = () =>
      items.filter((item) => !starFilter || Number(item.dataset.stars) === starFilter);

    const render = () => {
      const visible = matching();
      items.forEach((item) => {
        item.hidden = true;
      });
      visible.forEach((item, index) => {
        item.hidden = index >= shown;
      });

      if (moreBtn) {
        const extra = visible.length > pageSize;
        moreBtn.hidden = !extra;
        const expanded = extra && shown >= visible.length;
        moreBtn.textContent = expanded ? moreBtn.dataset.labelLess : moreBtn.dataset.labelMore;
        moreBtn.setAttribute('aria-expanded', String(expanded));
      }

      filters.forEach((btn) => {
        btn.setAttribute('aria-pressed', String(Number(btn.dataset.avStarFilter) === starFilter));
      });
    };

    moreBtn?.addEventListener('click', () => {
      const visible = matching();
      shown = shown >= visible.length ? pageSize : visible.length;
      render();
    });

    sortSelect?.addEventListener('change', () => {
      const mode = sortSelect.value;
      items = [...items].sort((a, b) => {
        if (mode === 'high') return Number(b.dataset.stars) - Number(a.dataset.stars);
        if (mode === 'low') return Number(a.dataset.stars) - Number(b.dataset.stars);
        return Number(a.dataset.index) - Number(b.dataset.index);
      });
      items.forEach((item) => list.appendChild(item));
      shown = pageSize;
      render();
    });

    filters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = Number(btn.dataset.avStarFilter);
        starFilter = starFilter === value ? 0 : value;
        shown = pageSize;
        render();
      });
    });

    list.querySelectorAll('[data-av-review-more]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const card = btn.closest('[data-av-review]');
        const open = card.classList.toggle('is-expanded');
        btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
      });
    });

    render();
  }

  headerOffset() {
    const header = document.querySelector('.av-header');
    const announcement = document.querySelector('.av-announcement');
    let offset = 0;
    // Only sticky chrome actually covers the target.
    if (header && getComputedStyle(header).position === 'sticky') {
      offset += header.getBoundingClientRect().height;
    }
    if (announcement && getComputedStyle(announcement).position === 'sticky') {
      offset += announcement.getBoundingClientRect().height;
    }
    return offset + 12;
  }

  findScrollContainer() {
    let element = this.root.parentElement;

    while (element && element !== document.body) {
      const overflowY = getComputedStyle(element).overflowY;
      const canScroll = /(auto|scroll|overlay)/.test(overflowY);
      if (canScroll && element.scrollHeight > element.clientHeight + 1) return element;
      element = element.parentElement;
    }

    return document.scrollingElement || document.documentElement;
  }

  usesWindowScroll() {
    return (
      this.scrollContainer === document.documentElement ||
      this.scrollContainer === document.body ||
      this.scrollContainer === document.scrollingElement
    );
  }

  scrollPosition() {
    return this.usesWindowScroll() ? window.scrollY : this.scrollContainer.scrollTop;
  }

  setScrollPosition(top) {
    const scrollingElement = this.usesWindowScroll()
      ? document.scrollingElement || document.documentElement
      : this.scrollContainer;
    const previousBehavior = scrollingElement.style.scrollBehavior;
    scrollingElement.style.scrollBehavior = 'auto';

    if (this.usesWindowScroll()) {
      window.scrollTo(0, top);
    } else {
      this.scrollContainer.scrollTop = top;
    }

    scrollingElement.style.scrollBehavior = previousBehavior;
  }

  scrollToOffer() {
    if (!this.offer) return;

    const start = this.scrollPosition();
    const viewportTop = this.usesWindowScroll()
      ? 0
      : this.scrollContainer.getBoundingClientRect().top;
    const target = Math.max(
      0,
      start + this.offer.getBoundingClientRect().top - viewportTop - this.headerOffset()
    );

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduceMotion ? 0 : this.ctaScrollSpeed;

    if (duration <= 0) {
      this.setScrollPosition(target);
      this.focusOffer();
      return;
    }

    const distance = target - start;
    const startedAt = performance.now();
    // easeOutCubic: leaves immediately, settles softly. Reads faster than
    // ease-in-out over the same duration because it front-loads the movement.
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const step = (now) => {
      const t = Math.min(1, (now - startedAt) / duration);
      this.setScrollPosition(start + distance * ease(t));
      if (t < 1) {
        window.requestAnimationFrame(step);
      } else {
        this.focusOffer();
      }
    };

    window.requestAnimationFrame(step);
  }

  /** Keyboard and screen-reader users need the focus to follow the scroll. */
  focusOffer() {
    const firstBundle = this.bundleInputs.find((input) => input.checked) || this.bundleInputs[0];
    if (!firstBundle) return;
    firstBundle.focus({ preventScroll: true });
  }

  async goToCheckout(cta) {
    const data = this.root.dataset;
    const variantId = data.variantId;
    const checkoutUrl = data.checkoutUrl || '/checkout';

    if (!variantId) {
      this.scrollToOffer();
      return;
    }

    const selected = this.bundleInputs.find((input) => input.checked);
    const quantity = Number.parseInt(selected?.value || data.defaultQuantity || '1', 10);

    cta.setAttribute('aria-busy', 'true');
    cta.classList.add('is-loading');

    try {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', String(quantity));

      const response = await fetch(data.cartAddUrl || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });

      if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

      // /cart/add can answer 200 with an error body when a line is rejected.
      // Navigating anyway would land on an empty checkout, which renders blank.
      let payload = null;
      try {
        payload = await response.clone().json();
      } catch (_) {
        payload = null;
      }
      if (payload && (payload.status >= 400 || payload.description)) {
        throw new Error(payload.description || 'Cart rejected the line');
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      // Never leave the customer on a dead button. Fall back to the on-page
      // chooser, which works without JavaScript at all.
      cta.removeAttribute('aria-busy');
      cta.classList.remove('is-loading');
      this.scrollToOffer();
    }
  }

  bindBundles() {
    this.bundleInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.bundleInputs.forEach((item) => {
          item.closest('.av-bundle')?.classList.toggle('is-selected', item.checked);
        });

        if (this.quantityInput) this.quantityInput.value = input.value;
        if (this.total) this.total.textContent = input.dataset.total || '';

        // Keep the sticky bar in sync so the persistent buy path always
        // reflects what the customer has actually selected.
        if (this.stickyTotal) this.stickyTotal.textContent = input.dataset.total || '';
        if (this.stickyQty) {
          const label = input.closest('.av-bundle')?.querySelector('.av-bundle__main strong');
          if (label) this.stickyQty.textContent = label.textContent.trim();
        }
      });
    });
  }

  bindGallery() {
    this.galleryIndex = Math.max(
      0,
      this.galleryThumbs.findIndex((thumb) => thumb.classList.contains('is-active'))
    );

    this.galleryThumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        this.showGalleryImage(index);
      });
    });

    this.galleryVideos.forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      const toggle = wrapper.querySelector('[data-av-gallery-video-toggle]');
      if (!video || !toggle) return;

      const syncToggle = () => toggle.setAttribute('aria-pressed', String(!video.paused));
      toggle.addEventListener('click', (event) => {
        if (this.isGallerySwipeClick()) {
          event.preventDefault();
          return;
        }
        this.toggleGalleryVideo(video);
      });
      video.addEventListener('play', syncToggle);
      video.addEventListener('pause', syncToggle);
      syncToggle();
    });

    this.root.querySelectorAll('[data-av-gallery-prev]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.showGalleryImage(this.galleryIndex - 1);
      });
    });
    this.root.querySelectorAll('[data-av-gallery-next]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.showGalleryImage(this.galleryIndex + 1);
      });
    });

    this.coarseGallery = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    this.bindGallerySwipe();

    this.galleryZoom?.addEventListener('click', (event) => {
      if (this.isGallerySwipeClick()) {
        event.preventDefault();
        return;
      }

      // Mobile: first tap reveals arrows; second tap opens fullscreen.
      if (this.coarseGallery && this.galleryStage && !this.galleryStage.classList.contains('is-nav-visible')) {
        event.preventDefault();
        this.galleryStage.classList.add('is-nav-visible');
        return;
      }
      this.openLightbox();
    });
    this.root.querySelectorAll('[data-av-gallery-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeLightbox());
    });
    this.root.querySelector('[data-av-gallery-lightbox-prev]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.showAdjacentLightboxImage(-1);
    });
    this.root.querySelector('[data-av-gallery-lightbox-next]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.showAdjacentLightboxImage(1);
    });

    if (this.coarseGallery) {
      this.lightbox?.addEventListener('pointerdown', (event) => {
        if (event.target.closest('[data-av-gallery-close]')) return;
        if (event.target.closest('.av-gallery__arrow')) return;
        this.lightbox.classList.add('is-nav-visible');
      });
    }

    if (this.galleryThumbs.length) this.showGalleryImage(this.galleryIndex);
  }

  bindGallerySwipe() {
    const enabled =
      this.coarseGallery || window.matchMedia('(max-width: 760px)').matches;
    if (!enabled || !this.galleryStage || this.galleryThumbs.length < 2) return;

    let start = null;

    this.galleryStage.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target.closest('.av-gallery__arrow')) return;
      if (event.target.closest('video, iframe')) return;

      start = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
      this.galleryStage.setPointerCapture?.(event.pointerId);
    });

    this.galleryStage.addEventListener('pointerup', (event) => {
      if (!start || event.pointerId !== start.id) return;

      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      start = null;
      this.galleryStage.releasePointerCapture?.(event.pointerId);

      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

      this.gallerySwipeHandledUntil = performance.now() + 500;
      this.showGalleryImage(this.galleryIndex + (deltaX < 0 ? 1 : -1));

      const activeThumb = this.galleryThumbs[this.galleryIndex];
      const rail = activeThumb?.parentElement;
      if (activeThumb && rail) {
        rail.scrollTo({
          left: activeThumb.offsetLeft - (rail.clientWidth - activeThumb.offsetWidth) / 2,
          behavior: 'smooth',
        });
      }
    });

    this.galleryStage.addEventListener('pointercancel', (event) => {
      if (start?.id === event.pointerId) start = null;
    });
  }

  isGallerySwipeClick() {
    return performance.now() < this.gallerySwipeHandledUntil;
  }

  showGalleryImage(index) {
    const sources = this.galleryThumbs;
    if (!sources.length) return;

    const total = sources.length;
    const nextIndex = ((index % total) + total) % total;
    const thumb = sources[nextIndex];
    if (!thumb) return;

    this.galleryIndex = nextIndex;

    sources.forEach((item, i) => {
      item.classList.toggle('is-active', i === nextIndex);
    });

    const mediaType = thumb.dataset.mediaType || 'image';
    this.showGalleryVideo(thumb, mediaType);

    if (mediaType !== 'image') {
      if (this.lightboxOpen) this.closeLightbox();
      return;
    }

    if (!this.galleryImage || !thumb.dataset.imageSrc) return;

    const stageSrc = thumb.dataset.imageSrc;
    const fullSrc = thumb.dataset.imageFull || stageSrc;

    // Lightbox path: swap immediately — no fade, no waiting on stage decode.
    if (this.lightboxOpen && this.lightboxImage) {
      this.lightboxImage.src = fullSrc;
      this.galleryImage.src = stageSrc;
      this.galleryImage.removeAttribute('srcset');
      this.galleryImage.style.opacity = '1';
      this.galleryImageCache.add(stageSrc);
      this.preloadAdjacent(nextIndex);
      return;
    }

    if (this.galleryImageCache.has(stageSrc)) {
      this.galleryImage.src = stageSrc;
      this.galleryImage.removeAttribute('srcset');
      this.galleryImage.style.opacity = '1';
      this.syncLightboxImage();
      this.preloadAdjacent(nextIndex);
      return;
    }

    this.galleryImage.style.opacity = '0';
    const replacement = new Image();
    replacement.onload = () => {
      this.galleryImageCache.add(stageSrc);
      this.galleryImage.src = stageSrc;
      this.galleryImage.removeAttribute('srcset');
      this.galleryImage.style.opacity = '1';
      this.syncLightboxImage();
    };
    replacement.src = stageSrc;
    this.preloadAdjacent(nextIndex);
  }

  showGalleryVideo(thumb, mediaType) {
    const activeIndex = thumb.dataset.mediaIndex;
    this.galleryVideos.forEach((wrapper) => {
      const shouldShow = mediaType !== 'image' && wrapper.dataset.avGalleryVideo === activeIndex;
      const wasVisible = !wrapper.hidden;

      if (wasVisible && !shouldShow) {
        wrapper.querySelectorAll('video').forEach((video) => video.pause());
        wrapper.querySelectorAll('iframe').forEach((frame) => {
          frame.src = frame.src;
        });
      }

      wrapper.hidden = !shouldShow;

      if (shouldShow) {
        const video = wrapper.querySelector('video');
        if (video?.paused) video.play().catch(() => {});
      }
    });

    if (this.galleryZoom) this.galleryZoom.hidden = mediaType !== 'image';
  }

  toggleGalleryVideo(video) {
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  showAdjacentLightboxImage(direction) {
    const total = this.galleryThumbs.length;
    if (!total) return;

    for (let offset = 1; offset <= total; offset += 1) {
      const index = (this.galleryIndex + direction * offset + total) % total;
      if ((this.galleryThumbs[index]?.dataset.mediaType || 'image') === 'image') {
        this.showGalleryImage(index);
        return;
      }
    }
  }

  preloadAdjacent(index) {
    if (!this.galleryThumbs.length) return;
    const total = this.galleryThumbs.length;
    [index - 1, index + 1].forEach((raw) => {
      const i = ((raw % total) + total) % total;
      const src = this.galleryThumbs[i]?.dataset.imageSrc;
      const full = this.galleryThumbs[i]?.dataset.imageFull;
      if (src && !this.galleryImageCache.has(src)) {
        const img = new Image();
        img.onload = () => this.galleryImageCache.add(src);
        img.src = src;
      }
      if (full) {
        const img = new Image();
        img.src = full;
      }
    });
  }

  syncLightboxImage() {
    if (!this.lightboxImage) return;

    const thumb = this.galleryThumbs[this.galleryIndex];
    if (thumb && (thumb.dataset.mediaType || 'image') !== 'image') return;
    const fullSrc =
      thumb?.dataset.imageFull ||
      thumb?.dataset.imageSrc ||
      this.galleryImage?.currentSrc ||
      this.galleryImage?.src;

    if (!fullSrc) return;
    this.lightboxImage.src = fullSrc;
    if (this.galleryImage?.alt) this.lightboxImage.alt = this.galleryImage.alt;
  }

  openLightbox() {
    if (!this.lightbox || !this.galleryImage) return;
    const activeThumb = this.galleryThumbs[this.galleryIndex];
    if (activeThumb && (activeThumb.dataset.mediaType || 'image') !== 'image') return;

    // Escape the hero grid stacking context so the bundle column cannot
    // paint over the overlay on desktop.
    if (this.lightbox.parentElement !== document.body) {
      document.body.appendChild(this.lightbox);
    }

    this.syncLightboxImage();
    this.lightbox.hidden = false;
    this.lightboxOpen = true;
    document.body.classList.add('av-gallery-lightbox-open');
    document.addEventListener('keydown', this.onLightboxKeydown);
    this.preloadAdjacent(this.galleryIndex);

    const coarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    this.lightbox.classList.toggle('is-nav-visible', coarsePointer);

    this.lightbox.querySelector('.av-gallery-lightbox__close')?.focus();
  }

  closeLightbox() {
    if (!this.lightbox || !this.lightboxOpen) return;

    this.lightbox.hidden = true;
    this.lightboxOpen = false;
    this.lightbox.classList.remove('is-nav-visible');
    document.body.classList.remove('av-gallery-lightbox-open');
    document.removeEventListener('keydown', this.onLightboxKeydown);
    this.root.querySelector('[data-av-gallery-open]')?.focus();
  }

  onLightboxKeydown(event) {
    if (!this.lightboxOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLightbox();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showAdjacentLightboxImage(-1);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showAdjacentLightboxImage(1);
    }
  }

  bindSticky() {
    if (!this.sticky || window.matchMedia('(min-width: 681px)').matches) return;

    let frameRequested = false;
    const update = () => {
      const offerRect = this.offer?.getBoundingClientRect();
      const finalRect = this.root.querySelector('.av-final')?.getBoundingClientRect();
      const footerRect = document.querySelector('.av-footer')?.getBoundingClientRect();
      const intersectsViewport = (rect) => rect && rect.top < window.innerHeight - 70 && rect.bottom > 80;
      const shouldShow =
        this.scrollPosition() > 320 &&
        !intersectsViewport(offerRect) &&
        !intersectsViewport(finalRect) &&
        !intersectsViewport(footerRect);

      this.sticky.classList.toggle('is-visible', shouldShow);
      this.sticky.setAttribute('aria-hidden', String(!shouldShow));
      frameRequested = false;
    };

    const requestUpdate = () => {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(update);
    };

    update();
    const scrollTarget = this.usesWindowScroll() ? window : this.scrollContainer;
    scrollTarget.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
  }

  bindCartEvents() {
    document.addEventListener('shopify:cart:lines-update', (event) => {
      if (event.action !== 'add') return;
      if (!(event.target instanceof Node) || !this.root.contains(event.target)) return;

      const cartDrawer = document.querySelector('#cart-drawer');
      const cartDrawerAutoOpens = Boolean(cartDrawer?.querySelector('cart-drawer-component[auto-open]'));
      let fallbackTimer;

      const finishLoading = () => {
        window.clearTimeout(fallbackTimer);
        cartDrawer?.removeEventListener('theme-drawer:open', handleDrawerOpen);
        this.addButton?.classList.remove('is-cart-loading');
        this.addButton?.removeAttribute('aria-busy');
      };

      const handleDrawerOpen = () => finishLoading();

      this.addButton?.classList.add('is-cart-loading');
      this.addButton?.setAttribute('aria-busy', 'true');

      // On the first add, cart-drawer.js deliberately waits for the populated
      // section before opening. Keep the spinner visible until that exact open
      // event so the normal label cannot flash back for a frame beforehand.
      if (cartDrawerAutoOpens && !cartDrawer?.hasAttribute('open')) {
        cartDrawer?.addEventListener('theme-drawer:open', handleDrawerOpen, { once: true });
      }

      event.promise
        ?.then(({ detail }) => {
          if (detail?.didError || !cartDrawerAutoOpens || cartDrawer?.hasAttribute('open')) {
            finishLoading();
            return;
          }

          // Safety net for a third-party drawer integration that suppresses the
          // standard open event. Never leave the purchase button stuck loading.
          fallbackTimer = window.setTimeout(finishLoading, 2500);
        })
        .catch(finishLoading);
    });
  }
}

/**
 * Scroll reveal.
 *
 * Section headings, cards and image slots start slightly low and transparent
 * and settle as they come into view. It is a small thing that makes a long
 * page feel like it is responding to the reader rather than sitting there.
 *
 * Three rules keep it from being the usual annoyance:
 *   - anything already on screen at load is revealed immediately, so nothing
 *     above the fold ever animates in;
 *   - each element is unobserved after it fires, so scrolling back up does not
 *     replay it;
 *   - prefers-reduced-motion, or a browser without IntersectionObserver, skips
 *     the whole thing and everything is simply visible.
 */
const REVEAL_SELECTOR = [
  '.av-section-heading',
  '.av-relief-card',
  '.av-belief__grid article',
  '.av-ingredient-card',
  '.av-evidence-card',
  '.av-routine__steps li',
  '.av-expectations__steps article',
  '.av-review',
  '.av-shortreview',
  '.av-badge',
  '.av-figure',
  '.av-compare__row',
  '.av-spiral__steps li',
  '.av-guarantee__seal',
].join(',');

const initAvenoriReveal = (root) => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) return;

  const targets = [...root.querySelectorAll(REVEAL_SELECTOR)];
  if (!targets.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  targets.forEach((el, index) => {
    if (el.getBoundingClientRect().top < viewportHeight) {
      el.classList.add('is-revealed');
      return;
    }
    // Siblings in the same row stagger by position rather than by index across
    // the whole page, so a grid of six cards ripples instead of arriving as a
    // block, and a card far down the page never carries a two-second delay.
    const siblingIndex = el.parentElement
      ? [...el.parentElement.children].indexOf(el)
      : index;
    el.style.setProperty('--av-reveal-delay', `${Math.min(siblingIndex, 5) * 60}ms`);
    el.classList.add('av-reveal');
    observer.observe(el);
  });
};

/**
 * Reading progress.
 *
 * A hairline across the top of the sticky header that fills as the page is
 * read. On a page this long it is the only thing that answers "how much of
 * this is left", and watching it fill is a small reward for continuing.
 *
 * Written straight to a custom property on a scroll listener with
 * requestAnimationFrame throttling — no layout reads inside the frame beyond a
 * single scrollHeight, which is cached until the page resizes.
 */
const initAvenoriProgress = (root) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (document.querySelector('[data-av-progress]')) return;

  const bar = document.createElement('div');
  bar.className = 'av-progress';
  bar.setAttribute('data-av-progress', '');
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);

  let max = 0;
  let ticking = false;

  const measure = () => {
    max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  };

  const paint = () => {
    const ratio = Math.min(1, Math.max(0, window.scrollY / max));
    bar.style.setProperty('--av-progress', ratio.toFixed(4));
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(paint);
  };

  measure();
  paint();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measure();
    paint();
  });
};

/**
 * Count-up on the numbers above the fold.
 *
 * The rating and the review count tick up once, the first time they scroll
 * into view. It is the oldest trick on a landing page and it still works,
 * because a number that moves is the only thing on a static page that behaves
 * like it is alive.
 *
 * The element's existing text is parsed rather than configured, so it picks up
 * whatever the merchant typed — including a thousands separator — and puts it
 * back in the same format at the end. If anything about the string is not a
 * number it is left alone.
 */
const initAvenoriCountUp = (root) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const targets = [...root.querySelectorAll('[data-av-count]')].filter((el) => {
    const raw = el.textContent.trim();
    return /^[\d.,]+$/.test(raw);
  });
  if (!targets.length) return;

  const run = (el) => {
    const raw = el.textContent.trim();
    const decimals = (raw.split(/[.,]/)[1] || '').length && !raw.includes(',') ? 1 : 0;
    const grouped = raw.includes(',') && !raw.match(/,\d$/);
    const target = parseFloat(raw.replace(/,/g, ''));
    if (!isFinite(target)) return;

    const format = (n) =>
      grouped ? Math.round(n).toLocaleString('en-US') : n.toFixed(decimals);

    const duration = 900;
    const start = performance.now();
    el.style.minWidth = `${el.getBoundingClientRect().width}px`;
    el.style.display = 'inline-block';

    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic: fast at first, settles rather than stops
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = format(target * eased);
      if (t < 1) window.requestAnimationFrame(step);
      else el.textContent = raw;
    };
    window.requestAnimationFrame(step);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        run(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.6 }
  );

  targets.forEach((el) => observer.observe(el));
};

const initAvenoriFunnels = (scope = document) => {
  scope.querySelectorAll('[data-avenori-funnel]').forEach((root) => {
    if (root.dataset.avReady === 'true') return;
    root.dataset.avReady = 'true';
    new AvenoriFunnel(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAvenoriFunnels());
} else {
  initAvenoriFunnels();
}

document.addEventListener('shopify:section:load', (event) => initAvenoriFunnels(event.target));
